import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface PropertyOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

interface Props {
  value: string;
  options: PropertyOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  leading?: ReactNode;
}

export default function PropertyMenu({ value, options, onChange, ariaLabel, leading }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  return (
    <div ref={rootRef} style={{ position: 'relative', minWidth: 0 }}>
      <button
        type="button"
        className="ln-prop-trigger"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((currentOpen) => !currentOpen)}
      >
        {leading}
        {current?.icon}
        <span>{current?.label ?? '—'}</span>
      </button>
      {open ? (
        <div className="ln-popover" role="menu" style={{ top: 30, right: 0, minWidth: 200 }}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="ln-popover-item"
              data-active={option.value === value ? 'true' : 'false'}
              role="menuitem"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.icon}
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
